import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  ArrowBack as BackIcon,
  ExitToApp as ExitIcon
} from '@mui/icons-material';
import { format, formatDistance } from 'date-fns';
import api from '../utils/api';

// Types
interface Visitor {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  purpose: string;
  badge_id: string;
  status: 'checked-in' | 'checked-out';
  host_id: number;
  host_name: string;
  check_in_time: string;
  check_out_time: string | null;
  photo_url: string | null;
  notes: string;
}

const CheckOut: React.FC = () => {
  // State
  const [visitor, setVisitor] = useState<Visitor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  // Hooks
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  // Fetch visitor data
  useEffect(() => {
    const fetchVisitor = async () => {
      try {
        if (!id) {
          setError('Visitor ID is required');
          setLoading(false);
          return;
        }
        
        const response = await api.get(`/api/visitors/${id}`);
        
        // Check if visitor is already checked out
        if (response.data.status === 'checked-out') {
          setError('This visitor has already been checked out');
          setLoading(false);
          return;
        }
        
        setVisitor(response.data);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching visitor:', err);
        setError(err.response?.data?.message || 'Failed to load visitor information');
        setLoading(false);
      }
    };
    
    fetchVisitor();
  }, [id]);
  
  // Handle notes change
  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(e.target.value);
  };
  
  // Open confirm dialog
  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };
  
  // Close confirm dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };
  
  // Handle checkout
  const handleCheckOut = async () => {
    if (!visitor) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    setConfirmDialogOpen(false);
    
    try {
      await api.post(`/api/visitors/${visitor.id}/check-out`, {
        notes: notes
      });
      
      setSuccess(true);
      setIsSubmitting(false);
    } catch (err: any) {
      console.error('Error checking out visitor:', err);
      setSubmitError(err.response?.data?.message || 'Failed to check out visitor. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  // Calculate duration of visit
  const calculateDuration = (checkInTime: string) => {
    return formatDistance(new Date(checkInTime), new Date(), { addSuffix: false });
  };
  
  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Render error state
  if (error || !visitor) {
    return (
      <Box>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Visitor Check-Out
        </Typography>
        
        <Paper sx={{ p: 3, mt: 3 }}>
          <Alert severity="error">
            {error || 'Visitor not found'}
          </Alert>
          
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/visitors')}
            >
              Back to Visitors
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
  
  // Render success state
  if (success) {
    return (
      <Box>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Visitor Check-Out
        </Typography>
        
        <Paper sx={{ p: 3, mt: 3, textAlign: 'center' }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              bgcolor: 'success.main', 
              mx: 'auto',
              mb: 2
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 40 }} />
          </Avatar>
          
          <Typography variant="h5" gutterBottom>
            Check-Out Successful!
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            {visitor.name} has been checked out successfully.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/visitors')}
            >
              View All Visitors
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
  
  // Check if visitor is already checked out
  if (visitor.status === 'checked-out') {
    return (
      <Box>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Visitor Check-Out
        </Typography>
        
        <Paper sx={{ p: 3, mt: 3 }}>
          <Alert severity="info">
            This visitor has already been checked out at {visitor.check_out_time ? format(new Date(visitor.check_out_time), 'PPpp') : 'unknown time'}.
          </Alert>
          
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/visitors')}
            >
              Back to Visitors
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Visitor Check-Out
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Process a visitor's check-out from your organization.
        </Typography>
      </Box>
      
      {/* Visitor information */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar 
                src={visitor.photo_url || undefined} 
                sx={{ 
                  width: 64, 
                  height: 64,
                  mr: 2,
                  bgcolor: theme.palette.primary.main
                }}
              >
                {!visitor.photo_url && <PersonIcon fontSize="large" />}
              </Avatar>
              
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {visitor.name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip 
                    label="Checked In" 
                    color="success" 
                    size="small" 
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Badge: {visitor.badge_id}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Visit Information
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2">
                        Checked in: {format(new Date(visitor.check_in_time), 'PPpp')}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <EventIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2">
                        Duration: {calculateDuration(visitor.check_in_time)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2">
                        Purpose: {visitor.purpose}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Contact Information
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {visitor.email || 'Not provided'}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> {visitor.phone || 'Not provided'}
                    </Typography>
                    
                    <Typography variant="body2">
                      <strong>Company:</strong> {visitor.company || 'Not provided'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Host Information
                    </Typography>
                    
                    <Typography variant="body2" fontWeight="medium">
                      {visitor.host_name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Check-Out Process
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add any notes about the visit before completing the check-out process.
            </Typography>
            
            {submitError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {submitError}
              </Alert>
            )}
            
            <TextField
              label="Check-Out Notes"
              value={notes}
              onChange={handleNotesChange}
              fullWidth
              multiline
              rows={4}
              sx={{ mb: 3 }}
              placeholder="Add any notes about the visit (optional)"
            />
            
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<ExitIcon />}
              onClick={handleOpenConfirmDialog}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Complete Check-Out'}
            </Button>
            
            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<BackIcon />}
              onClick={() => navigate('/visitors')}
              sx={{ mt: 2 }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
      >
        <DialogTitle>
          Confirm Check-Out
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to check out {visitor.name}? This will record their departure time and change their status to "Checked Out".
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCheckOut} color="primary" variant="contained">
            Confirm Check-Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CheckOut;
